import React, { useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { useTagAutocomplete } from '../hooks/useTagAutocomplete';
import type { WorkloadTagPair } from '../../../../types/workload';

interface TagPairEditorProps {
  tagPairs: WorkloadTagPair[];
  onChange: (pairs: WorkloadTagPair[]) => void;
}

const TagPairEditor: React.FC<TagPairEditorProps> = ({ tagPairs, onChange }) => {
  const { keySuggestions, valueSuggestions, searchKeys, searchValues } = useTagAutocomplete();
  const [activeKeyIndex, setActiveKeyIndex] = useState<number | null>(null);
  const [activeValueIndex, setActiveValueIndex] = useState<number | null>(null);

  const updatePair = (index: number, field: keyof WorkloadTagPair, value: string | null) => {
    const updated = [...tagPairs];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const addPair = () => {
    const newPairs = [...tagPairs];
    // Set operator on the current last pair before adding new one
    if (newPairs.length > 0) {
      newPairs[newPairs.length - 1] = { ...newPairs[newPairs.length - 1], operator: 'AND' };
    }
    newPairs.push({ tag_key: '', tag_value: '', operator: null });
    onChange(newPairs);
  };

  const removePair = (index: number) => {
    if (tagPairs.length <= 1) return;
    const newPairs = tagPairs.filter((_, i) => i !== index);
    // Clear operator on the new last pair
    if (newPairs.length > 0) {
      newPairs[newPairs.length - 1] = { ...newPairs[newPairs.length - 1], operator: null };
    }
    onChange(newPairs);
  };

  const toggleOperator = (index: number) => {
    const current = tagPairs[index].operator;
    updatePair(index, 'operator', current === 'AND' ? 'OR' : 'AND');
  };

  return (
    <div className="space-y-2">
      {tagPairs.map((pair, index) => (
        <div key={index}>
          <div className="flex items-center gap-2">
            {/* Key input */}
            <div className="relative flex-1">
              <input
                type="text"
                value={pair.tag_key}
                onChange={(e) => {
                  updatePair(index, 'tag_key', e.target.value);
                  searchKeys(e.target.value);
                  setActiveKeyIndex(index);
                  setActiveValueIndex(null);
                }}
                onFocus={() => {
                  searchKeys(pair.tag_key);
                  setActiveKeyIndex(index);
                  setActiveValueIndex(null);
                }}
                onBlur={() => setTimeout(() => setActiveKeyIndex(null), 200)}
                placeholder="Tag key"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {activeKeyIndex === index && keySuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {keySuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        updatePair(index, 'tag_key', suggestion);
                        setActiveKeyIndex(null);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="text-gray-400 dark:text-gray-500 text-sm">=</span>

            {/* Value input */}
            <div className="relative flex-1">
              <input
                type="text"
                value={pair.tag_value}
                onChange={(e) => {
                  updatePair(index, 'tag_value', e.target.value);
                  searchValues(pair.tag_key, e.target.value);
                  setActiveValueIndex(index);
                  setActiveKeyIndex(null);
                }}
                onFocus={() => {
                  if (pair.tag_key) searchValues(pair.tag_key, pair.tag_value);
                  setActiveValueIndex(index);
                  setActiveKeyIndex(null);
                }}
                onBlur={() => setTimeout(() => setActiveValueIndex(null), 200)}
                placeholder="Tag value"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {activeValueIndex === index && valueSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {valueSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        updatePair(index, 'tag_value', suggestion);
                        setActiveValueIndex(null);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Remove button */}
            {tagPairs.length > 1 && (
              <button
                type="button"
                onClick={() => removePair(index)}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                title="Remove tag pair"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* AND/OR operator between pairs */}
          {index < tagPairs.length - 1 && (
            <div className="flex justify-center my-1">
              <button
                type="button"
                onClick={() => toggleOperator(index)}
                className={`px-3 py-0.5 text-xs font-semibold rounded-full transition-colors ${
                  pair.operator === 'OR'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}
              >
                {pair.operator || 'AND'}
              </button>
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addPair}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
      >
        <FiPlus className="w-4 h-4" />
        Add Tag
      </button>
    </div>
  );
};

export default TagPairEditor;
